import { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Money } from "./SaudiRiyal";
import { api } from "../lib/api";

const fmt = (v) => Number(v || 0).toLocaleString("en");
const GOSI = { "تأمينات اجتماعية سعوديين": { rate: 20, who: "السعوديين" }, "تأمين مقيمين": { rate: 8, who: "المقيمين" } };
export const isGosiCat = (c) => !!GOSI[c];

const Row = ({ label, n, q, v, tone = "text-slate-700", tid }) => (
  <div className={`flex items-center justify-between gap-2 ${tone}`} data-testid={tid}>
    <span>{label}</span><span dir="ltr" className="font-mono-nums">{n} × {fmt(q)} = <b><Money value={v} /></b></span>
  </div>
);

const RenewalInfo = ({ r, residents, fee, amount }) => {
  if (!r) return null;
  const q = r.quarter_fee;
  return (
    <div className="space-y-1 rounded-lg bg-white p-2.5 text-xs" data-testid="work-permits-info">
      {r.overdue > 0 && <Row label="منتهية ولم تُجدَّد" n={r.overdue} q={q} v={r.overdue * q} tone="text-rose-700" tid="wp-overdue" />}
      <Row label="تنتهي هذا الشهر" n={r.this_month} q={q} v={r.this_month * q} tid="wp-this-month" />
      <div className="flex justify-between border-t border-slate-100 pt-1 font-bold text-indigo-800" data-testid="wp-due-now"><span>المطلوب هذا الشهر</span><Money value={amount} /></div>
      <Row label="الشهر القادم" n={r.next_month} q={q} v={r.next_amount} tone="text-slate-500" tid="wp-next-month" />
      <p className="pt-1 text-[11px] text-slate-500">تجديد 3 أشهر = {fmt(fee)} ÷ 4 = {fmt(q)} لكل مقيم · السنوي: {residents} مقيم × {fmt(fee)} = {fmt(r.annual)}</p>
      {r.missing > 0 && <p className="text-[11px] font-semibold text-amber-700" data-testid="wp-missing">⚠️ {r.missing} مقيم بدون تاريخ انتهاء إقامة — غير محسوبين</p>}
    </div>
  );
};

export const WorkPermitsBox = ({ initial, category, onSynced }) => {
  const g = GOSI[category || initial.category];
  const def = g ? g.rate : /رخص|كرت/.test(initial.name || "") || (category || initial.category) === "كرت العمل" ? 9700 : 650;
  const [fee, setFee] = useState(initial.per_resident_fee || def);
  const [info, setInfo] = useState(initial.resident_count != null ? { residents: initial.resident_count, amount: initial.last_payment_amount, salary_base: initial.salary_base, renewal: initial.renewal } : null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = Number(fee) !== Number(initial.per_resident_fee || def) ? await api.workPermitsFee(initial.id, Number(fee)) : await api.workPermitsSync(initial.id);
      setInfo(r); onSynced(r);
      toast.success(g ? `تمت المطابقة: ${r.salary_base.toLocaleString("en")} × ${r.fee}% = ${r.amount.toLocaleString("en")} شهرياً` : `تمت المطابقة: تجديدات هذا الشهر ${fmt(r.amount)} ريال`);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت المطابقة"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 space-y-2" data-testid="work-permits-box">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-indigo-800"><Users className="h-4 w-4" /> {g ? `حساب تلقائي: ${fee}% من الراتب الأساسي لـ${g.who} (شهرياً)` : "حساب تلقائي: الإقامات المنتهية هذا الشهر × تجديد 3 أشهر"}</p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-slate-600">{g ? "النسبة %" : "الرسوم السنوية للمقيم"}</label>
        <Input type="number" min="0.1" max={g ? 100 : undefined} step={g ? "0.25" : "1"} value={fee} onChange={(e) => setFee(e.target.value)} className="h-9 w-28" data-testid="work-permits-fee" />
        <Button type="button" size="sm" disabled={busy} onClick={run} className="gap-1 bg-indigo-600 hover:bg-indigo-700" data-testid="work-permits-sync">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} مطابقة
        </Button>
      </div>
      {info && (g
        ? <p className="text-xs text-slate-600" data-testid="work-permits-info">{g.who}: <b>{info.residents}</b> · مجموع الرواتب الأساسية: <b><Money value={info.salary_base || 0} /></b> · المبلغ الشهري: <b><Money value={info.amount} /></b></p>
        : <RenewalInfo r={info.renewal} residents={info.residents} fee={fee} amount={info.amount} />)}
      <p className="text-[11px] text-slate-500">{g ? "يتحدّث المبلغ تلقائياً عند تغيير الرواتب أو إضافة موظف أو تحويله إلى «منتهي الخدمة»." : "يتحدّث تلقائياً من تواريخ انتهاء إقامات الموظفين — حدّث تاريخ الإقامة بعد التجديد لتنتقل للشهر الصحيح."}</p>
    </div>
  );
};
